import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Search as SearchIcon } from 'lucide-react';
import { useMarkets } from '../../hooks/useMarkets';
import { useSearches } from '../../hooks/useSearches';
import { Market } from '../../types/market';
import { MarketForm } from './MarketForm';
import { SearchForm } from './SearchForm';
import { MarketSearches } from './MarketSearches';

export const AdminMarkets: React.FC = () => {
  const { 
    markets, 
    marketSearches, 
    createMarket, 
    updateMarket, 
    deleteMarket,
    deleteMarketSearch 
  } = useMarkets();
  const { deleteSearch } = useSearches();
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);
  const [showMarketForm, setShowMarketForm] = useState(false);
  const [showSearchForm, setShowSearchForm] = useState(false);

  const handleCreateMarket = async (data: Omit<Market, 'id'>) => {
    await createMarket(data);
    setShowMarketForm(false);
  };

  const handleUpdateMarket = async (data: Market) => {
    await updateMarket(data);
    setSelectedMarket(null);
    setShowMarketForm(false);
  };

  const handleDeleteMarket = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this market?')) {
      await deleteMarket(id);
    }
  };

  const handleDeleteSearch = async (marketSearchId: string, searchId: string) => {
    if (window.confirm('Are you sure you want to delete this search?')) {
      try {
        // First delete the market search connection
        await deleteMarketSearch(marketSearchId);
        // Then delete the search itself
        await deleteSearch(searchId);
      } catch (error) {
        console.error('Error deleting search:', error);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Markets Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Markets</h2>
          <button
            onClick={() => setShowMarketForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Market
          </button>
        </div>

        <div className="space-y-4">
          {markets.map(market => (
            <div
              key={market.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">{market.name}</h3>
                  {market.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{market.description}</p>
                  )}
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {marketSearches.filter(s => s.marketId === market.id).length} searches
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setSelectedMarket(market);
                      setShowSearchForm(true);
                    }}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    title="Add Search"
                  >
                    <SearchIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedMarket(market);
                      setShowMarketForm(true);
                    }}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    title="Edit Market"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteMarket(market.id)}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete Market"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Market Searches */}
              <MarketSearches
                market={market}
                searches={marketSearches.filter(s => s.marketId === market.id)}
                onDeleteSearch={handleDeleteSearch}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Market Form Modal */}
      {showMarketForm && (
        <MarketForm
          market={selectedMarket}
          onSubmit={selectedMarket ? handleUpdateMarket : handleCreateMarket}
          onClose={() => {
            setShowMarketForm(false);
            setSelectedMarket(null);
          }}
        />
      )}

      {/* Search Form Modal */}
      {showSearchForm && selectedMarket && (
        <SearchForm
          market={selectedMarket}
          onClose={() => {
            setShowSearchForm(false);
            setSelectedMarket(null);
          }}
        />
      )}
    </div>
  );
};